import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import { useBlogPosts } from './hooks/useBlogPosts';
import Comments from './components/Comments';
import './Blog.css';

export default function Blog() {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [allPosts, setAllPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  
  // Use React Query for caching
  const { data, isLoading } = useBlogPosts(14, offset);
  
  // Update posts when data changes
  useEffect(() => {
    if (data) {
      if (offset === 0) {
        // Initial load
        setAllPosts(data);
      } else {
        // Load more - append to existing posts
        setAllPosts(prevPosts => [...prevPosts, ...data]);
      }
      
      // Check if there are more posts to load
      const limit = offset === 0 ? 14 : 12;
      if (data.length < limit) {
        setHasMore(false);
      }
    }
  }, [data, offset]);

  const loadMore = () => {
    // First load was 14 posts, subsequent loads are 12
    const increment = offset === 0 ? 14 : 12;
    const newOffset = offset + increment;
    setOffset(newOffset);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (isLoading && offset === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <LoadingSpinner message="Loading posts..." />
      </div>
    );
  }


  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '2rem 1rem'
    }}>

      {allPosts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#666'
        }}>
          <LoadingSpinner message="Loading blog posts..." />
        </div>
      ) : (
        <>
          {/* Featured posts - First two posts in larger format */}
          <div className="blog-featured-posts" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            {allPosts.slice(0, 2).map((post) => {
              const excerptIsHtml = post.excerpt && (/</.test(post.excerpt) || /&/.test(post.excerpt));
              let truncatedExcerpt = post.excerpt;
              if (!excerptIsHtml && post.excerpt && post.excerpt.length > 150) {
                const substring = post.excerpt.substring(0, 150);
                const lastSpaceIndex = substring.lastIndexOf(' ');
                truncatedExcerpt = lastSpaceIndex > 0 
                  ? post.excerpt.substring(0, lastSpaceIndex) + '...' 
                  : substring + '...';
              }

              return (
                <article
                  key={post.id}
                  onClick={() => navigate(`/blog/${post.slug}?showComments=true`)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  {post.imageUrl && (
                    <div style={{
                      width: '100%',
                      height: '300px',
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5'
                    }}>
                      <img 
                        src={post.imageUrl} 
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div style={{
                    padding: '2rem',
                    paddingTop: '1.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1
                  }}>
                    <h2 style={{
                      fontSize: '1.75rem',
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '0.75rem',
                      lineHeight: '1.3'
                    }}>
                      {post.title}
                    </h2>
                    
                    {truncatedExcerpt && (
                      excerptIsHtml ? (
                        <div
                          style={{
                            fontSize: '1.05rem',
                            color: '#666',
                            lineHeight: '1.5',
                            marginBottom: '1rem',
                            flex: 1
                          }}
                          className="blog-card-excerpt-html"
                          dangerouslySetInnerHTML={{ __html: truncatedExcerpt }}
                        />
                      ) : (
                        <p style={{
                          fontSize: '1.05rem',
                          color: '#666',
                          lineHeight: '1.5',
                          marginBottom: '1rem',
                          flex: 1
                        }}>
                          {truncatedExcerpt}
                        </p>
                      )
                    )}
                    
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginTop: 'auto'
                    }}>
                      {formatDate(post.date)}
                    </div>
                  </div>
                  
                  {/* Comments Section */}
                  <div style={{ padding: '0 2rem 2rem 2rem' }}>
                    <Comments 
                      contentId={post.id} 
                      contentType="blog-post"
                      countOnly={true}
                      onAddCommentClick={() => {
                        const state = { showComments: true, showForm: true };
                        navigate(`/blog/${post.slug}`, { state });
                      }}
                      onShowCommentsClick={() => {
                        const state = { showComments: true, showForm: false };
                        navigate(`/blog/${post.slug}`, { state });
                      }}
                    />
                  </div>
                </article>
              );
            })}
          </div>

          {/* Regular posts - Remaining posts in standard grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {allPosts.slice(2).map((post) => {
            const excerptIsHtml = post.excerpt && (/</.test(post.excerpt) || /&/.test(post.excerpt));
            let truncatedExcerpt = post.excerpt;
            if (!excerptIsHtml && post.excerpt && post.excerpt.length > 150) {
              const substring = post.excerpt.substring(0, 150);
              const lastSpaceIndex = substring.lastIndexOf(' ');
              truncatedExcerpt = lastSpaceIndex > 0 
                ? post.excerpt.substring(0, lastSpaceIndex) + '...' 
                : substring + '...';
            }

            return (
              <article
                key={post.id}
                onClick={() => navigate(`/blog/${post.slug}?showComments=true`)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                {post.imageUrl && (
                  <div style={{
                    width: '100%',
                    height: '200px',
                    overflow: 'hidden',
                    backgroundColor: '#f5f5f5'
                  }}>
                    <img 
                      src={post.imageUrl} 
                      alt={post.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        // Show placeholder if image fails to load
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div style={{
                  padding: '1.5rem',
                  paddingTop: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1
                }}>
                  <h2 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '0.5rem',
                    lineHeight: '1.3'
                  }}>
                    {post.title}
                  </h2>
                  
                  {truncatedExcerpt && (
                    excerptIsHtml ? (
                      <div
                        style={{
                          fontSize: '0.95rem',
                          color: '#666',
                          lineHeight: '1.5',
                          marginBottom: '1rem',
                          flex: 1
                        }}
                        className="blog-card-excerpt-html"
                        dangerouslySetInnerHTML={{ __html: truncatedExcerpt }}
                      />
                    ) : (
                      <p style={{
                        fontSize: '0.95rem',
                        color: '#666',
                        lineHeight: '1.5',
                        marginBottom: '1rem',
                        flex: 1
                      }}>
                        {truncatedExcerpt}
                      </p>
                    )
                  )}
                  
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginTop: 'auto'
                  }}>
                    {formatDate(post.date)}
                  </div>
                </div>
                
                {/* Comments Section */}
                <div style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
                  <Comments 
                    contentId={post.id} 
                    contentType="blog-post"
                    countOnly={true}
                    onAddCommentClick={() => {
                      const state = { showComments: true, showForm: true };
                      navigate(`/blog/${post.slug}`, { state });
                    }}
                    onShowCommentsClick={() => {
                      const state = { showComments: true, showForm: false };
                      navigate(`/blog/${post.slug}`, { state });
                    }}
                  />
                </div>
              </article>
            );
          })}
          </div>
        </>
      )}

      {!isLoading && allPosts.length > 0 && hasMore && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '3rem',
          marginBottom: '2rem'
        }}>
          <button
            onClick={loadMore}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#ccc' : '#38b44a',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#2d8f3a';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#38b44a';
              }
            }}
          >
            {isLoading && <LoadingSpinner size="small" message="" />}
            {isLoading ? 'Loading...' : 'Load Older Posts'}
          </button>
        </div>
      )}
    </div>
  );
}

